import {PaginatedRequest} from "../../../../models/core/paginated.request";
import {PaginatedResult, Pagination} from "../../../../models/core/paginated.result";
import {useMemo, useState} from "react";
import {useURLParams} from "../../../../hooks/url-params.hook";
import {FieldStateGroup} from "../../../../models/filter/filter.field.state";
import {fromValues, getSnapshotFromUrl, toValues} from "../filtered-data.helpers";

export interface Values<T> {
    pagination?: Pagination;
    data: T[];
    onPageChange: (page: number, limit: number) => void;
    setFilters: (state: FieldStateGroup[], filters: any) => void;
    onFiltersInit?: (stateGroups: FieldStateGroup[], requestFilters: { [key: string]: any }) => void;
    snapshots: any;
}

export const useFilteredData = (
    dataLoader: (request: PaginatedRequest) => Promise<PaginatedResult<any>>
): Values<any> => {

    const {urlParams, setURLParams} = useURLParams();
    const [request, setRequest] = useState(fromValues(urlParams));
    const [pagination, setPagination] = useState<Pagination>();
    const [data, setData] = useState<any[]>([]);
    const [filterStateGroups, setFilterStateGroups] = useState<FieldStateGroup[]>([])
    const snapshots = useMemo(() => urlParams.filter ? getSnapshotFromUrl(urlParams.filter as string) : [], []);

    const reload = useMemo(() => (request: PaginatedRequest) => {
        dataLoader(request).then(({meta, items}) => {
            setData(items)
            setPagination(meta)
        });
    }, [])

    const onPageChange = (page: number, limit: number) => {
        const newRequest = new PaginatedRequest();
        newRequest.page = page;
        newRequest.limit = limit;
        newRequest.extraFields = request.extraFields;
        setRequest(newRequest)
        setURLParams(toValues(newRequest, filterStateGroups) as any)
        reload(newRequest);
    }

    const setFilters = (state: FieldStateGroup[], filters: any) => {
        const newRequest = new PaginatedRequest(filters);
        setRequest(newRequest)
        setFilterStateGroups(state)
        setURLParams(toValues(newRequest, state) as any)
        reload(newRequest);
    }

    const onFiltersInit = (stateGroups: FieldStateGroup[], requestFilters: { [key: string]: any }) => {
        setFilterStateGroups(stateGroups);
        const newRequest = new PaginatedRequest(requestFilters);
        reload(newRequest);
    }

    return {
        pagination: pagination,
        data: data,
        snapshots: snapshots,
        onPageChange: onPageChange,
        onFiltersInit: onFiltersInit,
        setFilters: setFilters,
    }
}